<template>
    <main>
        <h1>Event calendar</h1>
        <BaseSection>
            <BaseFilterEvent
                v-model="filters"
                category-type="liveTypes"
                :show-location="true"
                @input="handleInput"
            />
        </BaseSection>

        <BaseSection>
            <BaseCardCollection
                v-if="pending || (cards && cards.length)"
                :cards="cards || []"
                :wide="true"
                :placeholder="pending"
            />
            <p v-else>Sorry, there are no items to show here right now.</p>
        </BaseSection>

        <BaseSection>
            <BasePaginator
                :current-page="currentPage"
                :total-pages="Math.ceil(total / 16)"
                @update:current-page="currentPage = $event"
            />
        </BaseSection>
    </main>
</template>

<script setup>
import LiveQuery from '~/graphql/queries/events/Live.graphql';
import BasePaginator from '~/components/BasePaginator/BasePaginator.vue';
import useSrcSets from '~/utils/srcset';

const { activeSite } = useMultisite();
const { getSrcSet } = useSrcSets();

const currentPage = useCurrentPage();

const { variables } = useCraftVariables();
variables.uri = undefined;

variables.limit = 16;

const route = useRoute();

watchEffect(() => {
    variables.offset = route.query.page
        ? (route.query.page - 1) * variables.limit
        : 0;

    // Watch topic
    if (route.query.topic) {
        variables.relatedToEntries = [
            { section: 'topics', slug: route.query.topic },
        ];
    }

    // Watch type
    if (route.query.type) {
        variables.relatedToCategories = [
            { group: 'liveTypes', slug: route.query.type },
        ];
    }

    // Watch location
    if (route.query.location) {
        variables.city =
            route.query.location === 'online' ? 'Online' : ['not', 'Online'];
    }
});

const response = await useLazyAsyncQuery({
    query: LiveQuery,
    variables,
});

const { craftEntry: items, pending } = usePageQuery(response, {
    entryKey: 'entries',
    seoTitle: 'Education Events Calendar',
    activeSite
});

const { makeMeta, makeLocation } = useEventsCard();
const { getCardPills } = usePillsCard();

const cards = computed(() => {
    if (items?.value ?? items?.value?.length) {
        return items.value.map((event) => {
            let image = null;
            if (event.image.length) {
                image = {
                    src: event.image?.[0]?.large,
                    srcset: getSrcSet(event.image?.[0]),
                };
            }
            return {
                image,
                title: event.title,
                meta: makeMeta(event),
                location: makeLocation(event),
                uri: event.uri,
                pills: getCardPills(event),
            };
        });
    }
    return null;
});

const total = computed(() => response?.data?.value?.entryCount);

watch(
    () => currentPage.value,
    () => {
        window.scrollTo(0, 0);
    }
);

// eslint-disable-next-line vue/require-prop-types
const filters = defineModel();

// onMounted(() => {
filters.value = {
    topic: route.query.topic,
    type: route.query.type,
    location: route.query.location,
};

const handleInput = (newValue) => {
    filters.value = { ...newValue };
};

watchEffect(() => {
    if (filters.value.topic) {
        variables.relatedToEntries = [
            { section: 'topics', slug: filters.value.topic },
        ];
    } else {
        variables.relatedToEntries = undefined;
    }
    if (filters.value.type) {
        variables.relatedToCategories = [
            { group: 'liveTypes', slug: filters.value.type },
        ];
    } else {
        variables.relatedToCategories = undefined;
    }

    if (filters.value.location?.length) {
        // Hardcode is or is not city "online"
        variables.city =
            filters.value.location === 'online' ? 'Online' : ['not', 'Online'];
    } else {
        variables.city = undefined;
    }

    if (filters.value.topic === null && variables.relatedToEntries) {
        delete variables.relatedToEntries;
    }
    if (filters.value.type === null && variables.relatedToCategories) {
        delete variables.relatedToCategories;
    }
});
</script>
